/**
 * Org Members page — v28-3-3
 *
 * Routes: /org/settings/members
 *
 * Features:
 * - Member table with inline role selector and remove button
 * - Invite form: email + role + seat_tier (billable roles only) + seat quota display
 * - Pending invites section with revoke button
 * - Join requests tab: approve modal with seat_tier selector
 * - CSV Bulk Import tab: drag-and-drop + preview + confirm
 * - Domain auto-join settings
 */

import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, Download, AlertCircle } from 'lucide-react';
import { api } from '../../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrgMember {
  user_id: number;
  name: string;
  email: string;
  roles: string[];
  joined_at: string | null;
  kyc_status: string;
}

interface OrgInvite {
  id: number;
  org_id: number;
  email: string;
  role: string;
  seat_tier: string;
  status: string;
  expires_at: string;
  created_at: string | null;
}

interface OrgSettings {
  id: number;
  name: string;
  domain_auto_join: string;
  allowed_domains: string[];
}

interface JoinRequest {
  id: number;
  org_id: number;
  user_id: number;
  user_email: string | null;
  user_name: string | null;
  status: string;
  created_at: string | null;
}

interface SeatSummary {
  pro_seats: number;
  pro_used: number;
  pro_pending: number;
  premium_seats: number;
  premium_used: number;
  premium_pending: number;
}

interface ImportPreviewRow {
  email: string;
  role: string;
  seat_tier: string;
}

interface ImportError {
  row: number;
  email: string;
  error: string;
}

interface ImportResult {
  created: number;
  errors: ImportError[];
  preview?: ImportPreviewRow[];
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const orgApi = {
  getSettings: () => api.request<OrgSettings>('/api/v1/orgs/settings'),
  getMembers: () => api.request<OrgMember[]>('/api/v1/orgs/members'),
  updateMemberRoles: (userId: number, roles: string[]) =>
    api.request<OrgMember>(`/api/v1/orgs/members/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roles }),
    }),
  removeMember: (userId: number) =>
    api.request<void>(`/api/v1/orgs/members/${userId}`, { method: 'DELETE' }),
  inviteMember: (email: string, role: string, seat_tier: string) =>
    api.request<OrgInvite>('/api/v1/orgs/members/invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role, seat_tier }),
    }),
  getInvites: () => api.request<OrgInvite[]>('/api/v1/orgs/invites'),
  revokeInvite: (inviteId: number) =>
    api.request<void>(`/api/v1/orgs/invites/${inviteId}`, { method: 'DELETE' }),
  getJoinRequests: () => api.request<JoinRequest[]>('/api/v1/orgs/join-requests'),
  approveJoinRequest: (id: number, seat_tier: string) =>
    api.request<JoinRequest>(`/api/v1/orgs/join-requests/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ seat_tier }),
    }),
  rejectJoinRequest: (id: number, reason?: string) =>
    api.request<JoinRequest>(`/api/v1/orgs/join-requests/${id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason ?? null }),
    }),
  updateSettings: (settings: Partial<OrgSettings>) =>
    api.request<OrgSettings>('/api/v1/orgs/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    }),
  getSeatSummary: () => api.request<SeatSummary>('/api/v1/orgs/seat-summary'),
  downloadCsvTemplate: () =>
    fetch('/api/v1/orgs/members/import/template', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }).then((r) => {
      if (!r.ok) throw new Error(`Template download failed (${r.status})`);
      return r.blob();
    }),
  uploadCsv: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.request<ImportResult>('/api/v1/orgs/members/import', { method: 'POST', body: form });
  },
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROLES = [
  { value: 'org_admin', label: 'Org Admin' },
  { value: 'finance_manager', label: 'Finance Manager' },
  { value: 'lead_analyst', label: 'Lead Analyst' },
  { value: 'developer', label: 'Developer' },
  { value: 'devops_sre', label: 'DevOps / SRE' },
  { value: 'member', label: 'Member' },
];

/** Roles that consume a paid seat (must match backend BILLABLE_ROLES) */
const BILLABLE_ROLES = new Set(['lead_analyst', 'member']);

const SEAT_TIER_OPTIONS = [
  { value: 'pro', label: 'Pro seat' },
  { value: 'premium', label: 'Premium seat' },
];

const DOMAIN_AUTO_JOIN_OPTIONS = [
  { value: 'off', label: 'Off (manual invite only)' },
  { value: 'request', label: 'Request (admin approval required)' },
  { value: 'auto_approve', label: 'Auto-approve (anyone with matching domain)' },
];

function roleBadge(role: string) {
  const colors: Record<string, string> = {
    org_admin: 'bg-purple-900/40 text-purple-300 border border-purple-700',
    finance_manager: 'bg-green-900/40 text-green-300 border border-green-700',
    lead_analyst: 'bg-blue-900/40 text-blue-300 border border-blue-700',
    developer: 'bg-yellow-900/40 text-yellow-300 border border-yellow-700',
    devops_sre: 'bg-orange-900/40 text-orange-300 border border-orange-700',
    member: 'bg-slate-700/40 text-slate-300 border border-slate-600',
  };
  const label = ROLES.find((r) => r.value === role)?.label ?? role;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[role] ?? 'bg-slate-700 text-slate-300'}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// SeatQuotaDisplay helper
// ---------------------------------------------------------------------------

function SeatQuotaDisplay({ summary, tier }: { summary: SeatSummary | undefined; tier: string }) {
  if (!summary) return null;
  const total = tier === 'pro' ? summary.pro_seats : summary.premium_seats;
  const used = tier === 'pro' ? summary.pro_used : summary.premium_used;
  const pending = tier === 'pro' ? summary.pro_pending : summary.premium_pending;
  const available = Math.max(0, total - used - pending);
  const label = tier === 'pro' ? 'Pro' : 'Premium';
  return (
    <p className={`text-xs mt-1 ${available === 0 ? 'text-red-400' : 'text-slate-400'}`}>
      {label} seats: {used + pending} of {total} used ({available} available)
    </p>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Tab = 'members' | 'invites' | 'join-requests' | 'bulk-import' | 'settings';

export default function OrgMembers() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('members');

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteSeatTier, setInviteSeatTier] = useState('pro');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  // Join request approval modal state
  const [approvingRequest, setApprovingRequest] = useState<JoinRequest | null>(null);
  const [approveSeatTier, setApproveSeatTier] = useState('pro');
  const [approveError, setApproveError] = useState<string | null>(null);

  // Settings state
  const [settingsDomainAutoJoin, setSettingsDomainAutoJoin] = useState('off');
  const [settingsAllowedDomains, setSettingsAllowedDomains] = useState('');
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsInitialized, setSettingsInitialized] = useState(false);

  // Bulk import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // Queries
  const membersQuery = useQuery({
    queryKey: ['org-members'],
    queryFn: orgApi.getMembers,
  });

  const settingsQuery = useQuery({
    queryKey: ['org-settings'],
    queryFn: orgApi.getSettings,
  });

  const seatSummaryQuery = useQuery({
    queryKey: ['org-seat-summary'],
    queryFn: orgApi.getSeatSummary,
    enabled: activeTab === 'members' || activeTab === 'join-requests',
    staleTime: 30_000,
  });

  // Pre-populate settings form once loaded
  React.useEffect(() => {
    if (settingsQuery.data && !settingsInitialized) {
      setSettingsDomainAutoJoin(settingsQuery.data.domain_auto_join);
      setSettingsAllowedDomains(settingsQuery.data.allowed_domains.join('\n'));
      setSettingsInitialized(true);
    }
  }, [settingsQuery.data, settingsInitialized]);

  const invitesQuery = useQuery({
    queryKey: ['org-invites'],
    queryFn: orgApi.getInvites,
    enabled: activeTab === 'invites',
  });

  const joinRequestsQuery = useQuery({
    queryKey: ['org-join-requests'],
    queryFn: orgApi.getJoinRequests,
    enabled: activeTab === 'join-requests',
  });

  // Derived values
  const currentDomainAutoJoin = settingsQuery.data?.domain_auto_join ?? 'off';

  // If join-requests tab is active but mode changes away from 'request', switch to members
  React.useEffect(() => {
    if (activeTab === 'join-requests' && currentDomainAutoJoin !== 'request') {
      setActiveTab('members');
    }
  }, [currentDomainAutoJoin, activeTab]);

  // Mutations
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, roles }: { userId: number; roles: string[] }) =>
      orgApi.updateMemberRoles(userId, roles),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-members'] }),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: number) => orgApi.removeMember(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-members'] }),
  });

  const inviteMutation = useMutation({
    mutationFn: ({ email, role, seat_tier }: { email: string; role: string; seat_tier: string }) =>
      orgApi.inviteMember(email, role, seat_tier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-invites'] });
      queryClient.invalidateQueries({ queryKey: ['org-seat-summary'] });
      setInviteEmail('');
      setInviteRole('member');
      setInviteSeatTier('pro');
      setInviteError(null);
      setInviteSuccess(true);
      setTimeout(() => setInviteSuccess(false), 3000);
    },
    onError: (err: Error) => {
      setInviteError(err.message);
    },
  });

  const revokeInviteMutation = useMutation({
    mutationFn: (inviteId: number) => orgApi.revokeInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-invites'] });
      queryClient.invalidateQueries({ queryKey: ['org-seat-summary'] });
    },
  });

  const approveJoinMutation = useMutation({
    mutationFn: ({ id, seat_tier }: { id: number; seat_tier: string }) =>
      orgApi.approveJoinRequest(id, seat_tier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-join-requests'] });
      queryClient.invalidateQueries({ queryKey: ['org-members'] });
      queryClient.invalidateQueries({ queryKey: ['org-seat-summary'] });
      setApprovingRequest(null);
      setApproveError(null);
    },
    onError: (err: Error) => setApproveError(err.message),
  });

  const rejectJoinMutation = useMutation({
    mutationFn: (id: number) => orgApi.rejectJoinRequest(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-join-requests'] }),
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (settings: { domain_auto_join: string; allowed_domains: string[] }) =>
      orgApi.updateSettings(settings),
    onSuccess: () => {
      setSettingsSaved(true);
      setSettingsError(null);
      setTimeout(() => setSettingsSaved(false), 3000);
    },
    onError: (err: Error) => setSettingsError(err.message),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => orgApi.uploadCsv(file),
    onSuccess: (result) => {
      setImportResult(result);
      setImportError(null);
      if (result.created > 0) {
        queryClient.invalidateQueries({ queryKey: ['org-invites'] });
        queryClient.invalidateQueries({ queryKey: ['org-seat-summary'] });
      }
    },
    onError: (err: Error) => {
      setImportError(err.message);
      setImportResult(null);
    },
  });

  // Handlers
  const handleSendInvite = (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    if (!inviteEmail.trim()) return;
    const seat_tier = BILLABLE_ROLES.has(inviteRole) ? inviteSeatTier : 'free';
    inviteMutation.mutate({ email: inviteEmail.trim(), role: inviteRole, seat_tier });
  };

  const handleSaveSettings = (e: React.FormEvent) => {
    e.preventDefault();
    const domains = settingsAllowedDomains
      .split(/[,\n]/)
      .map((d) => d.trim())
      .filter(Boolean);
    updateSettingsMutation.mutate({
      domain_auto_join: settingsDomainAutoJoin,
      allowed_domains: domains,
    });
  };

  const handleApprove = () => {
    if (!approvingRequest) return;
    setApproveError(null);
    approveJoinMutation.mutate({ id: approvingRequest.id, seat_tier: approveSeatTier });
  };

  const handleFileSelect = (file: File | null) => {
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      setImportError('Only .csv files are accepted.');
      return;
    }
    setImportFile(file);
    setImportResult(null);
    setImportError(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await orgApi.downloadCsvTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'import_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[BulkImport] template download failed:', err);
      setImportError((err as Error).message || 'Failed to download CSV template.');
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'members', label: `Members (${membersQuery.data?.length ?? '—'})` },
    { id: 'invites', label: 'Invites' },
    ...(currentDomainAutoJoin === 'request'
      ? [{ id: 'join-requests' as Tab, label: 'Join Requests' }]
      : []),
    { id: 'bulk-import', label: 'Bulk Import' },
    { id: 'settings', label: 'Domain Settings' },
  ];

  const isBillable = BILLABLE_ROLES.has(inviteRole);

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Organisation Members</h1>
        <p className="text-slate-400 text-sm mt-1">
          Manage team members, invites, and domain auto-join settings.
        </p>
      </div>

      {/* Tab bar — flex-wrap so tabs wrap on narrow screens (AC12) */}
      <div className="flex flex-wrap gap-1 mb-6 border-b border-slate-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
              activeTab === tab.id
                ? 'bg-slate-700 text-white border-b-2 border-blue-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* --- Members tab --- */}
      {activeTab === 'members' && (
        <div>
          {/* Invite form — full-width stacked layout (AC9) */}
          <div className="bg-slate-800 rounded-lg p-3 sm:p-6 mb-6 border border-slate-700">
            <h2 className="text-sm font-semibold text-slate-300 mb-3">Invite a Member</h2>
            <form onSubmit={handleSendInvite} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Email address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  required
                  className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              {/* seat_tier dropdown — only for billable roles (AC1) */}
              {isBillable && (
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Seat tier</label>
                  <select
                    value={inviteSeatTier}
                    onChange={(e) => setInviteSeatTier(e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
                  >
                    {SEAT_TIER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {/* Seat quota display (AC2) */}
                  <SeatQuotaDisplay summary={seatSummaryQuery.data} tier={inviteSeatTier} />
                </div>
              )}
              <div>
                <button
                  type="submit"
                  disabled={inviteMutation.isPending}
                  className="w-full sm:w-auto px-4 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm rounded font-medium"
                >
                  {inviteMutation.isPending ? 'Sending…' : 'Send Invite'}
                </button>
              </div>
            </form>
            {inviteError && (
              <p className="text-red-400 text-xs mt-2">{inviteError}</p>
            )}
            {inviteSuccess && (
              <p className="text-green-400 text-xs mt-2">Invite sent successfully.</p>
            )}
          </div>

          {/* Member table */}
          {membersQuery.isLoading ? (
            <div className="text-slate-400 text-sm">Loading members…</div>
          ) : membersQuery.error ? (
            <div className="text-red-400 text-sm">{(membersQuery.error as Error).message}</div>
          ) : (
            <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Member</th>
                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Role</th>
                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Joined</th>
                    <th className="text-right px-4 py-3 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(membersQuery.data ?? []).map((member) => (
                    <tr key={member.user_id} className="border-b border-slate-700/50 last:border-0 hover:bg-slate-700/30">
                      <td className="px-4 py-3">
                        <div className="font-medium text-white">{member.name}</div>
                        <div className="text-xs text-slate-400">{member.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 mb-1.5">
                          {(member.roles || []).map((r) => <React.Fragment key={r}>{roleBadge(r)}</React.Fragment>)}
                        </div>
                        <div className="space-y-0.5">
                          {ROLES.map((r) => (
                            <label key={r.value} className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(member.roles || []).includes(r.value)}
                                onChange={(e) => {
                                  const current = member.roles || [];
                                  const next = e.target.checked
                                    ? [...current, r.value]
                                    : current.filter((x) => x !== r.value);
                                  if (next.length > 0) {
                                    updateRoleMutation.mutate({ userId: member.user_id, roles: next });
                                  }
                                }}
                                className="accent-blue-500"
                              />
                              {r.label}
                            </label>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        {member.joined_at
                          ? new Date(member.joined_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => {
                            if (window.confirm(`Remove ${member.name} from the organisation?`)) {
                              removeMemberMutation.mutate(member.user_id);
                            }
                          }}
                          className="text-red-400 hover:text-red-300 text-xs underline"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(membersQuery.data?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                        No members yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* --- Invites tab --- */}
      {activeTab === 'invites' && (
        <div>
          {invitesQuery.isLoading ? (
            <div className="text-slate-400 text-sm">Loading invites…</div>
          ) : invitesQuery.error ? (
            <div className="text-red-400 text-sm">{(invitesQuery.error as Error).message}</div>
          ) : (
            <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Email</th>
                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Role</th>
                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Seat</th>
                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Status</th>
                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Expires</th>
                    <th className="text-right px-4 py-3 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(invitesQuery.data ?? []).map((invite) => (
                    <tr key={invite.id} className="border-b border-slate-700/50 last:border-0 hover:bg-slate-700/30">
                      <td className="px-4 py-3 text-white">{invite.email}</td>
                      <td className="px-4 py-3">{roleBadge(invite.role)}</td>
                      <td className="px-4 py-3 text-slate-400 text-xs capitalize">{invite.seat_tier}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          invite.status === 'pending'
                            ? 'bg-yellow-900/40 text-yellow-300'
                            : 'bg-slate-700 text-slate-400'
                        }`}>
                          {invite.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        {new Date(invite.expires_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {invite.status === 'pending' && (
                          <button
                            onClick={() => revokeInviteMutation.mutate(invite.id)}
                            className="text-red-400 hover:text-red-300 text-xs underline"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(invitesQuery.data?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                        No pending invites.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* --- Join Requests tab --- */}
      {activeTab === 'join-requests' && (
        <div>
          {joinRequestsQuery.isLoading ? (
            <div className="text-slate-400 text-sm">Loading join requests…</div>
          ) : joinRequestsQuery.error ? (
            <div className="text-red-400 text-sm">{(joinRequestsQuery.error as Error).message}</div>
          ) : (
            <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left px-4 py-3 text-slate-400 font-medium">User</th>
                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Status</th>
                    <th className="text-left px-4 py-3 text-slate-400 font-medium">Requested</th>
                    <th className="text-right px-4 py-3 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(joinRequestsQuery.data ?? []).map((jr) => (
                    <tr key={jr.id} className="border-b border-slate-700/50 last:border-0 hover:bg-slate-700/30">
                      <td className="px-4 py-3">
                        <div className="font-medium text-white">{jr.user_name ?? `User #${jr.user_id}`}</div>
                        {jr.user_email && <div className="text-xs text-slate-400">{jr.user_email}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-900/40 text-yellow-300">
                          {jr.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        {jr.created_at ? new Date(jr.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => {
                              setApprovingRequest(jr);
                              setApproveSeatTier('pro');
                              setApproveError(null);
                            }}
                            className="text-green-400 hover:text-green-300 text-xs underline"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => rejectJoinMutation.mutate(jr.id)}
                            className="text-red-400 hover:text-red-300 text-xs underline"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(joinRequestsQuery.data?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                        No pending join requests.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* --- Bulk Import tab --- */}
      {activeTab === 'bulk-import' && (
        <div className="space-y-6">
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg border border-slate-600 transition-colors"
            >
              <Download size={14} />
              Download CSV template
            </button>
          </div>

          {/* Drag-and-drop zone (AC4) */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const file = e.dataTransfer.files[0] ?? null;
              handleFileSelect(file);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`w-full min-h-[120px] border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors ${
              dragOver ? 'border-blue-400 bg-blue-900/10' : 'border-slate-600 bg-slate-800 hover:border-slate-400'
            }`}
          >
            <Upload size={28} className="text-slate-400" />
            {importFile ? (
              <p className="text-sm text-white">{importFile.name}</p>
            ) : (
              <>
                <p className="text-sm text-slate-300">Drag and drop a CSV file here</p>
                <p className="text-xs text-slate-500">or click to browse</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
            />
          </div>

          {importFile && !importResult && (
            <button
              onClick={() => uploadMutation.mutate(importFile)}
              disabled={uploadMutation.isPending}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm rounded-lg font-medium"
            >
              {uploadMutation.isPending ? 'Uploading…' : 'Upload and preview'}
            </button>
          )}

          {/* Upload error */}
          {importError && (
            <div className="flex items-start gap-2 p-3 bg-red-900/30 border border-red-700 rounded-lg">
              <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{importError}</p>
            </div>
          )}

          {/* Results */}
          {importResult && (
            <div className="space-y-4">
              {importResult.created > 0 && (
                <div className="p-3 bg-teal-900/30 border border-teal-700 rounded-lg">
                  <p className="text-sm text-teal-300 font-medium">
                    ✓ {importResult.created} invite{importResult.created !== 1 ? 's' : ''} created successfully.
                  </p>
                </div>
              )}

              {/* Per-row error table (AC5) */}
              {importResult.errors.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-2">
                    Validation errors ({importResult.errors.length} row{importResult.errors.length !== 1 ? 's' : ''})
                  </h3>
                  {/* overflow-x-auto wrapper (AC11) */}
                  <div className="overflow-x-auto rounded-lg border border-red-800">
                    <table className="w-full text-sm">
                      <thead className="bg-red-900/30">
                        <tr>
                          <th className="text-left px-4 py-2 text-slate-400 font-medium">Row</th>
                          <th className="text-left px-4 py-2 text-slate-400 font-medium">Email</th>
                          <th className="text-left px-4 py-2 text-slate-400 font-medium">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.errors.map((err) => (
                          <tr key={err.row} className="border-t border-red-800/50">
                            <td className="px-4 py-2 text-slate-400">{err.row}</td>
                            <td className="px-4 py-2 text-white">{err.email}</td>
                            <td className="px-4 py-2 text-red-300">{err.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Preview table of successful rows (AC6) */}
              {importResult.preview && importResult.preview.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-2">
                    Preview ({importResult.preview.length} row{importResult.preview.length !== 1 ? 's' : ''})
                  </h3>
                  <div className="overflow-x-auto rounded-lg border border-slate-700">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-800">
                        <tr>
                          <th className="text-left px-4 py-2 text-slate-400 font-medium">Email</th>
                          <th className="text-left px-4 py-2 text-slate-400 font-medium">Role</th>
                          <th className="text-left px-4 py-2 text-slate-400 font-medium">Seat tier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.preview.map((row, i) => (
                          <tr key={i} className="border-t border-slate-700/50">
                            <td className="px-4 py-2 text-white">{row.email}</td>
                            <td className="px-4 py-2 text-slate-300">{row.role}</td>
                            <td className="px-4 py-2 text-slate-300 capitalize">{row.seat_tier}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  setImportFile(null);
                  setImportResult(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="text-xs text-slate-400 hover:text-slate-200 underline"
              >
                Upload another file
              </button>
            </div>
          )}
        </div>
      )}

      {/* --- Settings tab --- */}
      {activeTab === 'settings' && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-3 sm:p-6 max-w-lg">
          <h2 className="text-base font-semibold text-white mb-4">Domain Auto-Join Settings</h2>
          <form onSubmit={handleSaveSettings} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Auto-join mode</label>
              <select
                value={settingsDomainAutoJoin}
                onChange={(e) => setSettingsDomainAutoJoin(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
              >
                {DOMAIN_AUTO_JOIN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Controls how users with matching email domains can join your org.
              </p>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Allowed domains
              </label>
              <textarea
                value={settingsAllowedDomains}
                onChange={(e) => setSettingsAllowedDomains(e.target.value)}
                placeholder="acmecapital.co.za&#10;acme.com"
                rows={3}
                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 font-mono"
              />
              <p className="text-xs text-slate-500 mt-1">
                One domain per line (or comma-separated). Public email providers (gmail.com, etc.) are blocked.
              </p>
            </div>

            {settingsError && (
              <p className="text-red-400 text-sm">{settingsError}</p>
            )}
            {settingsSaved && (
              <p className="text-green-400 text-sm">Settings saved.</p>
            )}

            <button
              type="submit"
              disabled={updateSettingsMutation.isPending}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm rounded font-medium"
            >
              {updateSettingsMutation.isPending ? 'Saving…' : 'Save Settings'}
            </button>
          </form>
        </div>
      )}

      {/* --- Join request approval modal (AC3, AC10) --- */}
      {approvingRequest && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="w-full max-w-md mx-4 sm:mx-auto bg-slate-800 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-1">Approve join request</h3>
            <p className="text-sm text-slate-400 mb-4">
              {approvingRequest.user_name ?? `User #${approvingRequest.user_id}`}
              {approvingRequest.user_email && ` — ${approvingRequest.user_email}`}
            </p>

            <div className="mb-4">
              <label className="block text-xs text-slate-400 mb-1">Seat tier</label>
              <select
                value={approveSeatTier}
                onChange={(e) => setApproveSeatTier(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500"
              >
                {SEAT_TIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <SeatQuotaDisplay summary={seatSummaryQuery.data} tier={approveSeatTier} />
            </div>

            {approveError && (
              <p className="text-red-400 text-xs mb-3">{approveError}</p>
            )}

            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <button
                onClick={() => { setApprovingRequest(null); setApproveError(null); }}
                className="flex-1 sm:flex-none px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={approveJoinMutation.isPending}
                className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium transition-colors text-sm"
              >
                {approveJoinMutation.isPending ? 'Approving…' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
